import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import DashboardHeader from '../components/DashboardHeader';
import './AuthForm.css';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [inactivityMessage, setInactivityMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showGreenBar, setShowGreenBar] = useState(true);

  useEffect(() => {
    // Check if redirected due to inactivity
    if (searchParams.get('reason') === 'inactivity') {
      setInactivityMessage('You were logged out due to 30 minutes of inactivity. Please login again.');
      // Clear the message after 10 seconds
      const timer = setTimeout(() => setInactivityMessage(''), 10000);
      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  // Hide green bar on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowGreenBar(window.scrollY === 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrorMessage(''); // Clear error when user starts typing
  };

  const handleSubmit = async e => {
    e.preventDefault();

    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (res.ok) {
        // Store token and admin flag (convert boolean to string)
        localStorage.setItem('token', data.token);
        localStorage.setItem('isAdmin', String(data.user.isAdmin));


        // --- CART SYNC LOGIC ---
        const localCart = JSON.parse(localStorage.getItem('cart') || '[]');
        if (localCart.length > 0 && !data.user.isAdmin) {
          // For each item in localCart, add to backend cart
          for (const item of localCart) {
            try {
              await fetch(`${process.env.REACT_APP_API}/api/cart`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${data.token}`,
                },
                body: JSON.stringify({ productId: item._id, quantity: item.quantity || 1 }),
              });
            } catch (err) {
              // Ignore individual add errors
            }
          }
          // Clear localStorage cart after sync
          localStorage.removeItem('cart');
        }
        // --- END CART SYNC LOGIC ---

        // Redirect based on role
        if (data.user.isAdmin) {
          navigate('/admin'); // Admin dashboard
        } else {
          navigate('/home'); // Regular user home
        }
      } else {
        // Handle error responses
        if (res.status === 403 && data.requiresVerification) {
          const resend = window.confirm(
            'Your email is not verified. Would you like us to resend the verification email?'
          );
          if (resend) {
            await handleResendVerification(formData.email);
          }
        } else if (res.status === 429) {
          // Rate limit error
          setErrorMessage(data.message || 'Too many login attempts. Please wait 5 minutes before trying again.');
        } else {
          setErrorMessage(data.message || 'Invalid credentials. Please try again.');
        }
      }
    } catch (err) {
      console.error('Login error:', err);
      setErrorMessage('Server error. Please try again later.');
    }
  };

  const handleResendVerification = async (email) => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API}/api/auth/resend-verification`,
        { email }
      );
      alert(res.data.message || 'Verification email sent! Please check your inbox.');
    } catch (error) {
      alert(
        error.response?.data?.message || 
        'Failed to resend verification email. Please try again.'
      );
    }
  };

  return (
    <div style={{ background: 'linear-gradient(180deg, #f8fdf9 0%, #f0f7f1 100%)', minHeight: '100vh', width: '100vw', display: 'flex', flexDirection: 'column' }}>
      {/* Green Header Bar - only visible at top */}
      {showGreenBar && (
        <div style={{ width: '100%', background: 'linear-gradient(90deg, #175c36 0%, #1a6b3f 100%)', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }}>
          {/* Empty green bar for accent */}
        </div>
      )}
      {/* Navbar always sticky at top */}
      <div style={{ 
        width: '100%', 
        background: 'rgba(255, 255, 255, 0.98)', 
        backdropFilter: 'blur(10px)', 
        boxShadow: '0 4px 16px rgba(0,0,0,0.10)', 
        position: 'sticky',
        top: 0, 
        zIndex: 9998,
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)'
      }}>
        <div style={{ width: '100%', maxWidth: '1800px', margin: '0 auto', background: 'transparent' }}>
          <DashboardHeader />
        </div>
      </div>

      <motion.div 
        className="auth-container"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.4 }}
        style={{ paddingTop: '40px', paddingBottom: '40px' }}
      >
        <motion.form 
          className="auth-form" 
          onSubmit={handleSubmit}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
        >
        <h2>Login</h2>
        {errorMessage && (
          <div style={{
            background: '#fee',
            border: '2px solid #f44',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            color: '#c00',
            fontSize: '0.9rem',
            textAlign: 'center',
            fontWeight: 500
          }}>
            {errorMessage}
          </div>
        )}
        {inactivityMessage && (
          <div style={{
            background: '#fef3c7',
            border: '2px solid #fbbf24',
            borderRadius: '8px',
            padding: '12px',
            marginBottom: '16px',
            color: '#92400e',
            fontSize: '0.9rem',
            textAlign: 'center',
            fontWeight: 500
          }}>
            ⏰ {inactivityMessage}
          </div>
        )}
        <input
          type="email"
          name="email"
          placeholder="Email"
          required
          onChange={handleChange}
        />
        <input
          type="password"
          name="password"
          placeholder="Password"
          required
          onChange={handleChange}
        />
        <button type="submit">Login</button>
        <div className="auth-links">
          <Link to="/forgot-password">Forgot Password?</Link>
        </div>
        {/* Continue as guest button */}
        <button
          type="button"
          className="browse-btn"
          onClick={() => navigate('/home')}
          aria-label="Continue as Guest"
        >
          🛒 Continue as Guest
        </button>
        <div className="auth-links">
          <Link to="/register">Create account</Link>
        </div>
        </motion.form>
      </motion.div>
    </div>
  );
}

export default Login;
